export interface Logger {
  info: (message: string, ...args: unknown[]) => void;
  warn: (message: string, ...args: unknown[]) => void;
  error: (
    message: string,
    error?: unknown,
    context?: Record<string, unknown>
  ) => void;
}

export type ErrorReporter = (
  message: string,
  error: unknown,
  context?: Record<string, unknown>
) => void;

// Module-level constant, not a function/getter. On web, webpack's DefinePlugin
// replaces `process.env.NODE_ENV` with a string literal at build time, letting
// Terser fold and dead-code-eliminate the branches below entirely out of the
// production bundle. On native, `process.env.NODE_ENV` is set at *runtime* by
// react-native/Libraries/Core/setUpGlobals.js based on `__DEV__` — Metro does
// not inline it, so the branches are skipped at runtime rather than removed
// from the bundle, but the behavior (no-op info/warn in production) is the
// same on both platforms. Do NOT wrap this in a function call or use
// `.includes()`/other method calls — that defeats web's constant folding.
const isProduction = process.env['NODE_ENV'] === 'production';

// Each app configures this once at startup with a platform-specific reporter
// (New Relic browser agent on web, New Relic mobile agent on native). Left as a
// no-op until configured so importing this lib never requires a platform SDK.
let reportError: ErrorReporter = () => undefined;

/** Register the platform-specific error reporter. Call once per app at startup. */
export const configureLogger = (reporter: ErrorReporter): void => {
  reportError = reporter;
};

export const logger: Logger = {
  info: (message, ...args) => {
    if (isProduction) return;
    console.info(message, ...args);
  },
  warn: (message, ...args) => {
    if (isProduction) return;
    console.warn(message, ...args);
  },
  error: (message, error, context) => {
    if (isProduction) {
      reportError(message, error, context);
      return;
    }
    console.error(message, error, context);
  },
};
